// Browser-compatible Database Manager for ACCTOO
// Combines SQLite, Firebase, and Sync functionality for browser environment

import BrowserSQLiteManager from './BrowserSQLiteManager.js';
import BrowserSyncManager from './BrowserSyncManager.js';
import BrowserDeviceManager from './BrowserDeviceManager.js';
import { db } from '../firebase.config.js';

class BrowserDatabaseManager {
  constructor() {
    this.sqliteManager = new BrowserSQLiteManager();
    this.deviceManager = new BrowserDeviceManager(this.sqliteManager);
    this.syncManager = null;
    this.isInitialized = false;
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  // Initialize database system
  async init(userId, companyId, deviceId) {
    try {
      console.log('🔄 Initializing browser database system...', { userId, companyId, deviceId });

      // Initialize SQLite
      await this.sqliteManager.init(userId, companyId, deviceId);

      // Initialize sync manager
      this.syncManager = new BrowserSyncManager(db, this.sqliteManager);

      // Check if device exists, if not create it
      try {
        await this.deviceManager.getDevice(deviceId);
      } catch (error) {
        // Device doesn't exist, create it
        await this.deviceManager.registerOwnerDevice(userId, companyId);
      }

      this.isInitialized = true;
      console.log('✅ Browser database system initialized successfully');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Browser database initialization failed:', error);
      throw error;
    }
  }

  // =============================================
  // USER AND COMPANY MANAGEMENT
  // =============================================

  // Create or get user
  async createUser(userData) {
    try {
      const { id, email, name, phone } = userData;
      
      await this.sqliteManager.insert('users', {
        id, email, name, phone, status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      console.log('✅ User created/updated:', id);
      return { success: true, userId: id };
    } catch (error) {
      console.error('❌ User creation failed:', error);
      throw error;
    }
  }

  // Create or get company
  async createCompany(companyData) {
    try {
      const {
        id, userId, firmName, gstin, address, city, state, pincode,
        contactNumber, email, pan, gstinType, companyType,
        bankName, bankAccount, bankIfsc, upiId
      } = companyData;

      await this.sqliteManager.insert('companies', {
        id, user_id: userId, firm_name: firmName, gstin, address, city, state, pincode,
        contact_number: contactNumber, email, pan, gstin_type: gstinType, company_type: companyType,
        bank_name: bankName, bank_account: bankAccount, bank_ifsc: bankIfsc, upi_id: upiId,
        status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString()
      });

      console.log('✅ Company created/updated:', id);
      return { success: true, companyId: id };
    } catch (error) {
      console.error('❌ Company creation failed:', error);
      throw error;
    }
  }

  // =============================================
  // DEVICE MANAGEMENT
  // =============================================

  // Register new device
  async registerDevice(deviceType, deviceName, counterId = '') {
    try {
      switch (deviceType) {
        case 'owner':
          return await this.deviceManager.registerOwnerDevice(
            this.sqliteManager.userId, 
            this.sqliteManager.companyId, 
            deviceName
          );
        case 'counter':
          return await this.deviceManager.registerCounterDevice(
            this.sqliteManager.companyId, 
            deviceName, 
            counterId
          );
        case 'mobile':
          return await this.deviceManager.registerMobileDevice(
            this.sqliteManager.companyId, 
            deviceName
          );
        default:
          throw new Error('Invalid device type');
      }
    } catch (error) {
      console.error('❌ Register device failed:', error);
      throw error;
    }
  }

  // Get company devices
  async getCompanyDevices() {
    try {
      return await this.deviceManager.getCompanyDevices(this.sqliteManager.companyId);
    } catch (error) {
      console.error('❌ Get company devices failed:', error);
      throw error;
    }
  }

  // Approve device
  async approveDevice(deviceId) {
    try {
      return await this.deviceManager.approveDevice(deviceId);
    } catch (error) {
      console.error('❌ Approve device failed:', error);
      throw error;
    }
  }

  // =============================================
  // BUSINESS DATA OPERATIONS
  // =============================================

  // Generic data operations
  async insert(tableName, data) {
    try {
      // Add sync context
      const dataWithContext = {
        ...data,
        company_id: this.sqliteManager.companyId,
        device_id: this.sqliteManager.deviceId,
        sync_status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Insert into SQLite
      const result = await this.sqliteManager.insert(tableName, dataWithContext);

      // Add to sync queue
      await this.sqliteManager.addToSyncQueue(tableName, data.id, 'create', dataWithContext);

      return result;
    } catch (error) {
      console.error(`❌ Insert failed for ${tableName}:`, error);
      throw error;
    }
  }

  async update(tableName, id, data) {
    try {
      // Add sync context
      const dataWithContext = {
        ...data,
        updated_at: new Date().toISOString()
      };

      // Update in SQLite
      const result = await this.sqliteManager.update(tableName, id, dataWithContext);

      // Add to sync queue
      await this.sqliteManager.addToSyncQueue(tableName, id, 'update', dataWithContext);

      return result;
    } catch (error) {
      console.error(`❌ Update failed for ${tableName}:`, error);
      throw error;
    }
  }

  async select(tableName, filters = {}) {
    try {
      // Add company filter
      const filtersWithCompany = {
        ...filters,
        company_id: this.sqliteManager.companyId
      };

      return await this.sqliteManager.select(tableName, filtersWithCompany);
    } catch (error) {
      console.error(`❌ Select failed for ${tableName}:`, error);
      throw error;
    }
  }

  async delete(tableName, id) {
    try {
      // Delete from SQLite
      const result = await this.sqliteManager.delete(tableName, id);

      // Add to sync queue
      await this.sqliteManager.addToSyncQueue(tableName, id, 'delete', { id });

      return result;
    } catch (error) {
      console.error(`❌ Delete failed for ${tableName}:`, error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    try {
      return await this.sqliteManager.generateBillNumber(billType);
    } catch (error) {
      console.error('❌ Generate bill number failed:', error);
      throw error;
    }
  }

  // =============================================
  // SYNC OPERATIONS
  // =============================================

  // Full sync
  async sync() {
    try {
      if (!this.syncManager) {
        throw new Error('Sync manager not initialized');
      }
      return await this.syncManager.fullSync();
    } catch (error) {
      console.error('❌ Sync failed:', error);
      throw error;
    }
  }

  // Get sync status
  async getSyncStatus() {
    try {
      if (!this.syncManager) {
        return { syncInProgress: false, lastSyncTime: null, pendingRecords: 0 };
      }
      return await this.syncManager.getSyncStatus();
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }

  // =============================================
  // BACKUP AND RESTORE
  // =============================================

  // Create backup
  async createBackup() {
    try {
      return await this.sqliteManager.createBackup();
    } catch (error) {
      console.error('❌ Create backup failed:', error);
      throw error;
    }
  }

  // Restore from backup
  async restoreFromBackup(backupKey) {
    try {
      // Load backup from localStorage
      const backupData = localStorage.getItem(backupKey);
      if (!backupData) {
        throw new Error('Backup not found');
      }

      // Close current database
      await this.sqliteManager.close();

      // Create new database with backup data
      const data = new Uint8Array(JSON.parse(backupData));
      this.sqliteManager.db = new this.sqliteManager.SQL.Database(data);

      console.log('✅ Database restored from backup:', backupKey);
      return { success: true };
    } catch (error) {
      console.error('❌ Restore from backup failed:', error);
      throw error;
    }
  }

  // =============================================
  // STATISTICS AND MONITORING
  // =============================================

  // Get database statistics
  async getDatabaseStats() {
    try {
      return await this.sqliteManager.getDatabaseStats();
    } catch (error) {
      console.error('❌ Get database stats failed:', error);
      throw error;
    }
  }

  // Get device statistics
  async getDeviceStats() {
    try {
      return await this.deviceManager.getDeviceStats(this.sqliteManager.companyId);
    } catch (error) {
      console.error('❌ Get device stats failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if initialized
  isReady() {
    return this.isInitialized;
  }

  // Get current context
  getContext() {
    return {
      userId: this.sqliteManager.userId,
      companyId: this.sqliteManager.companyId,
      deviceId: this.sqliteManager.deviceId,
      dbPath: this.sqliteManager.getDatabasePath()
    };
  }

  // Close database
  async close() {
    try {
      await this.sqliteManager.close();
      this.isInitialized = false;
      console.log('✅ Database closed');
    } catch (error) {
      console.error('❌ Close database failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const browserDatabaseManager = new BrowserDatabaseManager();

export default browserDatabaseManager;
